import React, { useState, useEffect } from 'react';
import styles from './ChatSearchDemo.module.css';

const ChatSearchDemo = () => {
  const [currentQuery, setCurrentQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [currentQueryIndex, setCurrentQueryIndex] = useState(0);

  const queries = [
    'Help me find tense footsteps for the chase scene in my thriller',
    'I need dark cinematic music that builds tension for the final act',
    'Looking for clean ambient sounds of a busy coffee shop in my library',
    'Can you find emotional piano music for the ending scene?',
    'Need realistic city traffic sounds for this exterior night shot'
  ];

  const soundResults = {
    'Help me find tense footsteps for the chase scene in my thriller': [
      { name: 'Running Footsteps Gravel', duration: '0:08' },
      { name: 'Heavy Boots Chase', duration: '0:12' },
      { name: 'Quick Steps Concrete', duration: '0:06' },
      { name: 'Panic Run Alley', duration: '0:10' }
    ],
    'I need dark cinematic music that builds tension for the final act': [
      { name: 'Rising Dread Orchestra', duration: '3:24' },
      { name: 'Tension Builder Dark', duration: '2:45' },
      { name: 'Suspense Strings Build', duration: '3:12' },
      { name: 'Ominous Crescendo', duration: '2:58' }
    ],
    'Looking for clean ambient sounds of a busy coffee shop in my library': [
      { name: 'Coffee Shop Morning', duration: '2:20' },
      { name: 'Cafe Chatter Clean', duration: '1:45' },
      { name: 'Espresso Machine Ambience', duration: '1:30' },
      { name: 'Busy Restaurant Background', duration: '2:05' }
    ],
    'Can you find emotional piano music for the ending scene?': [
      { name: 'Gentle Piano Reflection', duration: '3:15' },
      { name: 'Melancholic Keys', duration: '2:48' },
      { name: 'Heartfelt Piano Solo', duration: '3:30' },
      { name: 'Emotional Resolution', duration: '2:55' }
    ],
    'Need realistic city traffic sounds for this exterior night shot': [
      { name: 'City Traffic Night', duration: '1:45' },
      { name: 'Urban Street Ambience', duration: '2:10' },
      { name: 'Cars Passing Evening', duration: '1:30' },
      { name: 'Downtown Night Sounds', duration: '1:55' }
    ]
  };

  useEffect(() => {
    const query = queries[currentQueryIndex];
    let charIndex = 0;
    
    // Reset
    setCurrentQuery('');
    setShowResults(false);

    // Typewriter effect
    const typeInterval = setInterval(() => {
      if (charIndex < query.length) {
        setCurrentQuery(query.substring(0, charIndex + 1));
        charIndex++;
      } else {
        clearInterval(typeInterval);
        
        // Show results after typing completes
        setTimeout(() => {
          setShowResults(true);
          
          // Wait 3.5 seconds then move to next query
          setTimeout(() => {
            setCurrentQueryIndex((prev) => (prev + 1) % queries.length);
          }, 3500);
        }, 500);
      }
    }, 50);

    return () => clearInterval(typeInterval);
  }, [currentQueryIndex]);

  const results = soundResults[queries[currentQueryIndex]] || [];

  return (
    <div className={styles.chatSearchSection}>
      <div className={styles.chatSearchContainer}>
        <h2 className={styles.sectionTitle}>
          Can't Find It? Just Ask.
        </h2>
        <p className={styles.sectionSubtitle}>
          Natural language search across 20M+ assets and your library. No keywords, no browsing.
        </p>

        <div className={styles.demoCard}>
          {/* Chat Input */}
          <div className={styles.chatInput}>
            <div className={styles.inputIcon}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
            </div>
            <div className={styles.typingText}>
              {currentQuery}
              <span className={styles.cursor}>|</span>
            </div>
          </div>

          {/* Results Grid */}
          {showResults && (
            <div className={styles.resultsGrid}>
              {results.map((sound, idx) => (
                <div 
                  key={idx} 
                  className={styles.soundCard}
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <div className={styles.waveformIcon}>
                    <svg width="40" height="24" viewBox="0 0 40 24" fill="none">
                      {[...Array(8)].map((_, i) => (
                        <rect
                          key={i}
                          x={i * 5}
                          y={12 - Math.random() * 10}
                          width="3"
                          height={Math.random() * 20 + 4}
                          fill="url(#waveGradient)"
                          opacity="0.8"
                        />
                      ))}
                      <defs>
                        <linearGradient id="waveGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="#dd00ac" />
                          <stop offset="100%" stopColor="#7130c3" />
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>
                  <div className={styles.soundInfo}>
                    <div className={styles.soundName}>{sound.name}</div>
                    <div className={styles.soundDuration}>{sound.duration}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatSearchDemo;
